/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUCSuggestions } from '@atlassian/navigation-bff-data-provider';

import { useUniversalCreateState } from '../../../context';
import {
	useSuggestionExcludeAtis,
	useSuggestionNativeAtis,
	useSuggestionPreferredAtis,
	useUniversalCreateAnalytics,
} from '../../../hooks';
import { GenericErrorBoundary } from '../../../services';
import type { MixedMenuItems } from '../../../types';
import ItemWrapper from '../MenuItem/item-wrapper';
import MenuSectionPrimitive from '../MenuSectionPrimitive';
import { SkeletonItem } from '../SkeletonItem';
import { SuggestionItem } from '../SuggestionItem';

const messages = defineMessages({
	sectionHeading: {
		id: 'platform.navigation-experiences.universal-create.suggestion.section-heading',
		defaultMessage: '{count, plural, one {Suggestion} other {Suggestions}}',
		description: 'The heading for the suggestion menu section',
	},
});

function SuggestionMenuSectionContent() {
	const { formatMessage, locale } = useIntl();
	const { items, product, suggestionConfig, subProduct } = useUniversalCreateState();
	// SuggestionMenuSection expects MixedMenuItems (NamedItems[])
	const mixedItems = items as MixedMenuItems;

	const cloudId = suggestionConfig.enabled ? suggestionConfig.cloudId : '';
	const orgId = suggestionConfig.enabled ? suggestionConfig.orgId : '';
	const userId = suggestionConfig.enabled ? suggestionConfig.accountId : '';
	const compact = suggestionConfig.enabled && !!suggestionConfig.compact;
	const location = suggestionConfig.enabled ? suggestionConfig.location : 'end';

	// Use the shared hooks for ATIs logic
	const nativeAtis = useSuggestionNativeAtis();
	const preferredAtis = useSuggestionPreferredAtis();
	const excludeAtis = useSuggestionExcludeAtis();

	const { data, isLoading, hasError } = useUCSuggestions({
		cloudId,
		orgId,
		userId,
		product,
		subProduct,
		locale,
		nativeAtis,
		preferredAtis,
		excludeAtis,
	});

	const { fireNewSuggestionsMenuViewed } = useUniversalCreateAnalytics();

	useEffect(() => {
		const items = data?.items;
		if (items?.length) {
			const suggestedItems = items.map((item) => item.ati);
			fireNewSuggestionsMenuViewed({
				crossAppSuggestionsEnabled: suggestionConfig.enabled || false,
				suggestionsGroupCount: 0,
				suggestionsItemCount: items.length,
				suggestionGroupList: [
					{
						// Note: Currently only one suggestion section is supported, and more button is not in use
						groupHeading: formatMessage(messages.sectionHeading, { count: items.length }),
						groupIndex: 0,
						groupItemAtisList: suggestedItems,
						groupItemCount: items.length,
					},
				],
			});
		}
	}, [
		data,
		fireNewSuggestionsMenuViewed,
		formatMessage,
		compact,
		location,
		suggestionConfig.enabled,
	]);

	if (suggestionConfig.enabled && isLoading) {
		return (
			<ItemWrapper isMenuItem={!fg('navx-enghealth-47001-suggestion-loading-semantics')}>
				<SkeletonItem testId="loading-suggestion-section" />
			</ItemWrapper>
		);
	}

	if (!suggestionConfig.enabled || hasError || !data?.items?.length) {
		return null;
	}

	const suggestionItems = data.items;

	return (
		<MenuSectionPrimitive
			sectionIndex={location === 'start' ? 0 : mixedItems.length + 1}
			itemLength={suggestionItems.length}
			showAll
			compact={compact}
			heading={formatMessage(messages.sectionHeading, { count: suggestionItems.length })}
			isNative={false}
		>
			{suggestionItems.map((responseItem, index) => (
				<SuggestionItem
					key={`suggestion-menu-item-${responseItem.ati}`}
					responseItem={responseItem}
					context={suggestionConfig}
					product={product}
					subProduct={subProduct}
					itemIndex={index}
					totalItemsCount={suggestionItems.length}
				/>
			))}
		</MenuSectionPrimitive>
	);
}

export default function SuggestionMenuSection() {
	return (
		<GenericErrorBoundary name="suggestion-menu-section">
			<SuggestionMenuSectionContent />
		</GenericErrorBoundary>
	);
}
